import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Trash2, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const COUNT_LABELS = {
  studio_finance: "finanzas", studio_tasks: "tareas", studio_files: "archivos",
  studio_sprints: "sprints", studio_moodboards: "moodboards", studio_comments: "comentarios",
  studio_concept_boards: "conceptos", tagma_entries: "movimientos",
  flow_tasks: "tareas", flow_sprints: "sprints", flow_comments: "comentarios", flow_files: "archivos",
  gout_moodboards: "moodboards",
};

export const TrashDialog = ({ onClose, onChanged, recoverPath = null, testid = "trash-dialog" }) => {
  const [trash, setTrash] = useState(null);
  const [orphans, setOrphans] = useState(recoverPath ? null : []);
  const [busy, setBusy] = useState(null);
  const loadAll = () => {
    api.get("/trash").then((r) => setTrash(r.data)).catch(() => setTrash([]));
    if (recoverPath) api.get(recoverPath).then((r) => setOrphans(r.data.orphans)).catch(() => setOrphans([]));
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line
  const restore = async (t) => {
    setBusy(t.id);
    try {
      const r = await api.post(`/trash/${t.id}/restore`);
      toast.success(`"${r.data.name}" restaurado (${r.data.records} registro(s))`);
      loadAll(); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
    setBusy(null);
  };
  const recover = async () => {
    setBusy("recover");
    try {
      const r = await api.post(recoverPath);
      toast.success(`${r.data.recovered} campaña(s) recuperadas con sus datos — renómbralas desde su detalle`);
      loadAll(); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
    setBusy(null);
  };
  const countLabel = (c) => Object.entries(c || {}).map(([k, v]) => `${v} ${COUNT_LABELS[k] || k}`).join(" · ");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid={testid}
        className="w-full max-w-2xl bg-cream rounded-xl p-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-medium">Papelera y recuperación</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>

        <div className="overline text-navy/40 mb-2">Papelera · restaurable 30 días</div>
        {!trash ? <Loader2 className="w-4 h-4 animate-spin text-navy/30" /> : trash.length === 0 ? (
          <div className="text-xs text-navy/40 border border-dashed border-navy/15 rounded-lg p-4" data-testid="trash-empty">La papelera está vacía. Todo lo que se elimine (por chat o por botón) queda aquí 30 días.</div>
        ) : (
          <div className="space-y-2">
            {trash.map((t) => (
              <div key={t.id} className="flex items-center gap-3 bg-white border border-navy/10 rounded-lg px-4 py-3" data-testid={`trash-item-${t.id}`}>
                <Trash2 className="w-4 h-4 text-navy/25 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{t.name || "—"}</div>
                  <div className="text-[11px] text-navy/40">{t.label} · {(t.deleted_at || "").slice(0, 16).replace("T", " ")} · por {t.deleted_by_name || "—"}{t.cascade_count ? ` · incluye ${t.cascade_count} registro(s) vinculados` : ""}</div>
                </div>
                <button onClick={() => restore(t)} disabled={busy === t.id} data-testid={`trash-restore-${t.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50">
                  {busy === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Restaurar
                </button>
              </div>
            ))}
          </div>
        )}

        {recoverPath && (
          <>
            <div className="overline text-navy/40 mt-6 mb-2">Datos huérfanos detectados</div>
            {!orphans ? <Loader2 className="w-4 h-4 animate-spin text-navy/30" /> : orphans.length === 0 ? (
              <div className="text-xs text-navy/40 border border-dashed border-navy/15 rounded-lg p-4" data-testid="orphans-empty">No hay datos huérfanos: todas las finanzas, tareas y archivos están vinculados a una campaña existente.</div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-navy/50">Se encontraron finanzas/tareas/archivos cuya campaña fue borrada sin cascada. Al recuperar, se recrea cada campaña con su id original y recobras todo lo vinculado (renómbrala después desde su detalle).</p>
                {orphans.map((o) => (
                  <div key={o.project_id} className="bg-white border border-navy/10 rounded-lg px-4 py-3" data-testid={`orphan-${o.project_id}`}>
                    <div className="text-sm font-medium">{countLabel(o.counts)}</div>
                    <div className="text-[11px] text-navy/40 mt-0.5">{(o.samples || []).join(" · ") || "sin conceptos"}{o.oldest ? ` · desde ${o.oldest.slice(0, 10)}` : ""}</div>
                  </div>
                ))}
                <button onClick={recover} disabled={busy === "recover"} data-testid="tagma-recover-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 transition-opacity disabled:opacity-50">
                  {busy === "recover" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Recuperar {orphans.length} campaña(s) con sus datos
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export const TrashButton = ({ onClick, testid = "trash-btn" }) => (
  <button data-testid={testid} onClick={onClick}
    className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white text-navy/60 text-sm px-4 py-3 hover:border-navy/30 transition-colors">
    <RotateCcw className="w-4 h-4" /> Papelera
  </button>
);

export default TrashDialog;
